import React, { useEffect, useState } from "react";
import { Clock, Sparkle, Spinner, Trash, PlayCircle, Plus, ToggleRight, ToggleLeft } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";

export default function SavedSearches() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null);

  // Form state
  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("Roofing");
  const [city, setCity] = useState("Sioux Falls");
  const [state, setState] = useState("South Dakota");
  const [count, setCount] = useState(6);

  const load = async () => {
    const [m, l] = await Promise.all([api.get("/meta"), api.get("/saved-searches")]);
    setMeta(m.data);
    setItems(l.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name) { toast.error("Name required"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/saved-searches", { name, industry, city, state, count });
      setItems((p) => [data, ...p]);
      setName("");
      toast.success("Saved search created");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const runNow = async (s) => {
    setRunning((r) => ({ ...r, [s.id]: true }));
    try {
      const { data } = await api.post(`/saved-searches/${s.id}/run`);
      toast.success(`Created ${data.created.length} new leads${data.skipped_duplicates ? ` · ${data.skipped_duplicates} dupes skipped` : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Run failed");
    } finally {
      setRunning((r) => ({ ...r, [s.id]: false }));
    }
  };

  const toggle = async (s) => {
    try {
      const { data } = await api.patch(`/saved-searches/${s.id}`, { enabled: !s.enabled });
      setItems((p) => p.map((x) => (x.id === s.id ? data : x)));
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (s) => {
    try {
      await api.delete(`/saved-searches/${s.id}`);
      setItems((p) => p.filter((x) => x.id !== s.id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <AppShell>
      <PageHeader
        eyebrow="Automation"
        title="Saved Searches"
        subtitle="Define industry + city combos. The forge runs them every Monday at 8 AM Central — duplicates are skipped automatically."
        testid="saved-header"
      />

      <div className="px-4 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <form onSubmit={create} data-testid="saved-form" className="lg:col-span-4 bg-zinc-900/60 border border-zinc-800 rounded-sm p-5 space-y-4">
          <div className="flex items-center gap-2 text-orange-500">
            <Plus size={16} weight="bold" />
            <span className="font-mono text-xs uppercase tracking-wider">New saved search</span>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Name</Label>
            <Input data-testid="saved-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. SF Roofers" className="mt-1.5 bg-zinc-950 border-zinc-800" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Industry</Label>
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger data-testid="saved-industry-select" className="mt-1.5 bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                {(meta?.industries || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">City</Label>
            <Input data-testid="saved-city-input" value={city} onChange={(e) => setCity(e.target.value)} className="mt-1.5 bg-zinc-950 border-zinc-800" />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">State</Label>
            <Select value={state} onValueChange={setState}>
              <SelectTrigger data-testid="saved-state-select" className="mt-1.5 bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                {(meta?.midwest_states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Leads per run</Label>
            <Input data-testid="saved-count-input" type="number" min={1} max={15} value={count} onChange={(e) => setCount(Number(e.target.value))} className="mt-1.5 bg-zinc-950 border-zinc-800" />
          </div>
          <Button type="submit" data-testid="saved-create-btn" disabled={loading} className="w-full bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm h-10">
            {loading ? (<><Spinner size={14} className="mr-1.5 animate-spin" /> Saving…</>) : (<><Plus size={14} className="mr-1.5" /> Create saved search</>)}
          </Button>
        </form>

        <div className="lg:col-span-8 space-y-3">
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-4 flex items-center gap-3 text-sm text-zinc-300">
            <Clock size={16} className="text-orange-500" weight="duotone" />
            <span className="font-mono text-xs">Weekly cron: <strong className="text-orange-400">Mondays · 8:00 AM · America/Chicago</strong></span>
          </div>

          {items.length === 0 && (
            <div data-testid="saved-empty" className="bg-zinc-900/40 border border-zinc-800 rounded-sm px-6 py-12 text-center">
              <Sparkle size={28} weight="duotone" className="mx-auto text-zinc-700 mb-3" />
              <div className="text-sm text-zinc-400">No saved searches yet.</div>
              <div className="text-xs text-zinc-600 mt-1">Create one on the left to automate weekly lead discovery.</div>
            </div>
          )}

          {items.map((s) => (
            <div key={s.id} data-testid={`saved-row-${s.id}`} className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-medium text-zinc-50">{s.name}</span>
                    <span className={`stage-pill px-1.5 py-0.5 rounded-sm border ${s.enabled ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10" : "border-zinc-700 text-zinc-500 bg-zinc-900"}`}>
                      {s.enabled ? "ACTIVE" : "PAUSED"}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-400 font-mono mt-1">
                    {s.industry} · {s.city}, {s.state} · {s.count} leads/run
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5">
                    <span>Total runs: <strong className="text-zinc-300">{s.total_runs}</strong></span>
                    {s.last_run_at && <span>Last: <strong className="text-zinc-300">{new Date(s.last_run_at).toLocaleString()}</strong></span>}
                    {s.last_run_at && <span>+{s.last_created_count} new · {s.last_skipped_count} dupes</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-1.5">
                    <Switch
                      data-testid={`saved-toggle-${s.id}`}
                      checked={s.enabled}
                      onCheckedChange={() => toggle(s)}
                    />
                  </div>
                  <Button
                    size="sm"
                    data-testid={`saved-run-${s.id}`}
                    onClick={() => runNow(s)}
                    disabled={running[s.id]}
                    className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm"
                  >
                    {running[s.id] ? <Spinner size={12} className="animate-spin mr-1" /> : <PlayCircle size={14} className="mr-1" weight="fill" />}
                    Run now
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`saved-delete-${s.id}`}
                    onClick={() => setPendingDelete(s)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title={`Delete saved search "${pendingDelete?.name || ""}"?`}
        description="The weekly cron will stop running for this search. Existing leads it created are kept."
        confirmLabel="Delete"
        destructive
        onConfirm={() => pendingDelete && remove(pendingDelete)}
        testid="confirm-delete-saved"
      />
    </AppShell>
  );
}
